<template>
  <div class="vc-popover">
    <div v-if="$slots.default" ref="reference" class="vc-popover__trigger">
      <slot :open="open" :close="close" :toggle="toggle" :opened="opened" :trigger-props="emitTriggerProps" />
    </div>

    <div v-else-if="$slots.trigger" ref="reference" class="vc-popover__trigger" v-bind="emitTriggerProps">
      <slot name="trigger" :open="open" :close="close" :toggle="toggle" :opened="opened" />
    </div>

    <teleport :to="teleportSelector" :disabled="!shouldTeleport">
      <div
        v-if="$slots.content && !disabled && shouldRenderContent"
        :id="contentId"
        ref="floating"
        :style="{ zIndex, display, width, ...floatingStyles }"
        :class="[
          'vc-popover__body',
          {
            'vc-popover__body--shadow': shadow,
          },
        ]"
        :role="role"
        :aria-label="ariaLabel"
      >
        <div
          v-if="arrowEnabled"
          ref="floatingArrow"
          class="vc-popover__popper"
          :style="{
            left: arrowLeft,
          }"
        >
          <div class="vc-popover__arrow"></div>
        </div>

        <div class="vc-popover__content">
          <slot name="content" :close="close" />
        </div>
      </div>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { flip, offset, shift, useFloating, autoUpdate, arrow } from "@floating-ui/vue";
import { onClickOutside } from "@vueuse/core";
import { ref, toRefs, computed, watch, inject } from "vue";
import { useComponentId } from "@/ui-kit/composables";
import { getColorValue } from "@/ui-kit/utilities";
import { vcPopoverKey } from "./vc-popover-context";

interface IEmits {
  (event: "toggle", value: boolean): void;
}

interface IProps {
  placement?: VcPopoverPlacementType;
  strategy?: VcPopoverStrategyType;
  flipOptions?: VcPopoverFlipOptionsType;
  offsetOptions?: VcPopoverOffsetOptionsType;
  shiftOptions?: VcPopoverShiftOptionsType;
  disabled?: boolean;
  shadow?: boolean;
  bgColor?: string;
  radius?: string;
  width?: string;
  zIndex?: number | string;
  role?: string;
  hover?: boolean;
  disableTriggerEvents?: boolean;
  arrowEnabled?: boolean;
  ariaLabel?: string;
  enableTeleport?: boolean | null;
  teleportSelector?: string;
  lazy?: boolean;
}

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  zIndex: 1,
  placement: "bottom",
  teleportSelector: "[id='popover-host']",
  enableTeleport: null,
});

const popoverContext = inject(vcPopoverKey, null);
const shouldTeleport = computed(() =>
  props.enableTeleport === null ? (popoverContext?.enableTeleport.value ?? false) : props.enableTeleport,
);

const opened = ref(false);
const hasBeenOpened = ref(false);
const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);
const floatingArrow = ref<Element | null>(null);
const contentId = useComponentId("vc-popover");
const { placement, strategy, flipOptions, offsetOptions, shiftOptions } = toRefs(props);

const shouldRenderContent = computed(() => !props.lazy || hasBeenOpened.value);

const emitTriggerProps = computed(() => ({
  role: "button" as const,
  "aria-haspopup": "dialog" as const,
  "aria-expanded": opened.value,
  "aria-controls": opened.value ? contentId : undefined,
  onMouseenter: props.hover ? open : undefined,
  onMouseleave: props.hover ? close : undefined,
  onFocusin: props.hover ? open : undefined,
  onFocusout: props.hover ? close : undefined,
  onClick: props.hover ? undefined : toggle,
  onKeyup: (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      open();
    }
    if (e.key === "Escape") {
      close();
    }
  },
}));

const display = computed(() => (opened.value ? "block" : "none"));
const arrowLeft = computed(() => (middlewareData.value.arrow?.x != null ? `${middlewareData.value.arrow.x}px` : ""));
const _bgColor = computed(() => getColorValue(props.bgColor));
const _radius = computed(() => props.radius);

const { floatingStyles, middlewareData } = useFloating(reference, floating, {
  placement,
  strategy,
  transform: false,
  middleware: [
    flip(flipOptions.value),
    offset(offsetOptions.value),
    shift(shiftOptions.value),
    arrow({ element: floatingArrow }),
  ],
  whileElementsMounted(...args) {
    return autoUpdate(...args, { animationFrame: true });
  },
});

function open() {
  if (props.disabled) {
    return;
  }

  hasBeenOpened.value = true;
  opened.value = true;
}

function close() {
  if (props.disabled) {
    return;
  }

  opened.value = false;
}

function toggle() {
  if (props.disabled) {
    return;
  }

  if (!opened.value) {
    hasBeenOpened.value = true;
  }

  opened.value = !opened.value;
}

// Reactivity loss is acceptable here: teleportSelector is static after mount,
// and onClickOutside options are not reactive anyway
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
onClickOutside(reference, () => close(), { ignore: [floating, props.teleportSelector] });

watch(opened, (value: boolean) => emit("toggle", value));
</script>

<style lang="scss">
.vc-popover {
  $popper: "";
  $shadow: "";

  @apply max-w-full;

  &__trigger {
    @apply max-w-full size-full;
  }

  &__popper {
    $popper: &;

    @apply absolute top-0 w-5 h-2.5 p-1 overflow-hidden;
  }

  &__body {
    // For teleportation cases, variables are declared in &__body
    --props-bg-color: v-bind(_bgColor);
    --props-radius: v-bind(_radius);
    --bg-color: var(--props-bg-color, var(--vc-popover-bg-color, transparent));
    --radius: var(--props-radius, var(--vc-popover-radius, var(--vc-radius, 0.5rem)));
    --arrow-color: var(--props-bg-color, var(--vc-popover-bg-color, var(--color-additional-50)));

    @apply max-w-[100vw];

    &:has(#{$popper}) {
      @apply pt-2.5;
    }

    &--shadow {
      $shadow: &;
    }
  }

  &__arrow {
    @apply w-3 h-3 rotate-45 bg-[--arrow-color];

    #{$shadow} & {
      @apply shadow-md;
    }
  }

  &__content {
    @apply bg-[--bg-color] rounded-[--radius];

    #{$shadow} & {
      @apply shadow-lg;
    }
  }
}
</style>
